import { useState, useEffect } from 'react';
import { fetchCMSContent } from '../lib/cms';

export default function GallerySection({ slug }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      setLoading(true);
      const galleries = await fetchCMSContent('gallery');
      // Find galleriet baseret på slug (da slug er et sprogobjekt i CMS, f.eks. { da: 'erhvervs-vinduespolering' })
      const found = galleries.find(g => {
        const gSlug = typeof g.slug === 'object' ? g.slug.da : g.slug;
        return gSlug === slug;
      });
      setGallery(found || null);
      setLoading(false);
    }
    loadGallery();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue border-t-transparent" />
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <span className="text-xs font-bold uppercase tracking-widest text-brand-blue">
          Galleri
        </span>
        <h3 className="mt-2 font-display text-2xl font-bold text-brand-dark sm:text-3xl">
          Se vores arbejde: {gallery.title}
        </h3>
        {gallery.description && (
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-500">
            {gallery.description}
          </p>
        )}
      </div>

      {/* Billedgrid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:gap-6">
        {gallery.images.map((imgUrl, idx) => {
          // Optimer billedet med Cloudflare on-the-fly resizing (bredde 600px til kort)
          const optimizedUrl = imgUrl.includes('assets.auxodev.dk')
            ? imgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/')
            : imgUrl;

          return (
            <button
              key={idx}
              onClick={() => setActiveImageIndex(idx)}
              className="group relative aspect-4/3 overflow-hidden rounded-2xl bg-slate-100 shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              <img
                src={optimizedUrl}
                alt={`${gallery.title} - Billede ${idx + 1}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100 flex items-end justify-center pb-4">
                <span className="rounded-full bg-white/90 px-4 py-2 text-xs font-bold text-brand-dark flex items-center gap-1.5 shadow-sm">
                  <i className="fa-solid fa-magnifying-glass-plus text-brand-blue" />
                  Vis i stor
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox / Modal */}
      {activeImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm transition-opacity"
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Luk-knap */}
          <button
            onClick={() => setActiveImageIndex(null)}
            className="absolute top-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Luk"
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>

          {/* Forrige-knap */}
          <button
            onClick={handlePrev}
            className="absolute left-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Forrige"
          >
            <i className="fa-solid fa-chevron-left text-lg" />
          </button>

          {/* Billede */}
          <div className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={gallery.images[activeImageIndex]}
              alt={`${gallery.title} - Stor visning`}
              className="max-h-[80vh] max-w-full object-contain rounded-lg"
            />
            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-4 py-3 text-center text-xs text-white/90">
              Billede {activeImageIndex + 1} af {gallery.images.length}
            </div>
          </div>

          {/* Næste-knap */}
          <button
            onClick={handleNext}
            className="absolute right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Næste"
          >
            <i className="fa-solid fa-chevron-right text-lg" />
          </button>
        </div>
      )}
    </div>
  );
}
